import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import WorkingShiftAllocation from '../WorkingShiftAllocation';
import employeeForm7 from '../../assets/images/employees/employeeform7.png';
import { apiRequest } from '../../utils/api';
import { getActiveCompanyId } from '../../utils/companyContext';
import { normalizeTimeForPayload, toTimeInputValue } from '../../utils/time';

const mapInitialShifts = (source) => {
  if (!Array.isArray(source)) {
    return [];
  }

  return source.map((entry, index) => ({
    id: entry?.id ?? `existing-shift-${index}`,
    shiftId:
      entry?.shiftId === null || entry?.shiftId === undefined
        ? ''
        : String(entry.shiftId),
    timeFrom: toTimeInputValue(entry?.timeFrom ?? entry?.shiftInTime),
    timeTo: toTimeInputValue(entry?.timeTo ?? entry?.shiftOutTime),
    isPrimary: Boolean(entry?.isPrimary ?? (index === 0 && source.length > 0)),
    displayOrder: Number.isFinite(Number(entry?.displayOrder))
      ? Number(entry.displayOrder)
      : index,
  }));
};

const normalizeShiftsForPayload = (entries = []) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const sanitized = entries
    .map((entry, index) => {
      if (!entry) {
        return null;
      }

      const shiftId = entry.shiftId && String(entry.shiftId).trim() !== ''
        ? String(entry.shiftId)
        : '';
      const timeFrom = normalizeTimeForPayload(entry.timeFrom);
      const timeTo = normalizeTimeForPayload(entry.timeTo);

      if (!shiftId && !timeFrom && !timeTo) {
        return null;
      }

      return {
        id: entry.id,
        shiftId,
        timeFrom,
        timeTo,
        isPrimary: Boolean(entry.isPrimary),
        displayOrder: Number.isFinite(Number(entry.displayOrder))
          ? Number(entry.displayOrder)
          : index,
      };
    })
    .filter(Boolean);

  if (sanitized.length === 0) {
    return [];
  }

  const primaryIndex = sanitized.findIndex((entry) => entry.isPrimary);
  return sanitized.map((entry, index) => ({
    ...entry,
    displayOrder: index,
    isPrimary: primaryIndex === -1 ? index === 0 : index === primaryIndex,
  }));
};

const formatShiftOptionLabel = (shift) => {
  if (!shift) {
    return '';
  }

  const start = toTimeInputValue(shift.inTime ?? shift.shiftInTime);
  const end = toTimeInputValue(shift.outTime ?? shift.shiftOutTime);

  if (shift.shiftName) {
    if (start || end) {
      return `${shift.shiftName} (${start || '—'} - ${end || '—'})`;
    }
    return shift.shiftName;
  }

  if (start || end) {
    return `${start || '—'} - ${end || '—'}`;
  }

  return 'Shift';
};

const WorkingTimeForm = ({ onSubmit, data, enableValidation = false, companyId = getActiveCompanyId() }) => {
  const navigate = useNavigate();
  const [shiftOptions, setShiftOptions] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [shiftError, setShiftError] = useState(null);

  const initialValues = useMemo(
    () => ({
      type: data?.type ?? '',
      shifts: mapInitialShifts(data?.shifts),
    }),
    [data?.type, data?.shifts],
  );

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: initialValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const fetchShiftOptions = useCallback(async () => {
    try {
      setLoadingShifts(true);
      setShiftError(null);
      if (!companyId) {
        setShiftOptions([]);
        return;
      }
      const params = new URLSearchParams({ companyId: String(companyId) });
      const response = await apiRequest(`/api/admin/settings/shifts?${params.toString()}`, {}, navigate);
      const rows = Array.isArray(response.data) ? response.data : [];
      const options = rows
        .filter((shift) => {
          const status = String(shift?.status ?? '').toLowerCase();
          return status !== 'inactive';
        })
        .map((shift) => ({
          value: String(shift.id),
          label: formatShiftOptionLabel(shift),
          inTime: shift.inTime ?? null,
          outTime: shift.outTime ?? null,
        }));
      setShiftOptions(options);
    } catch (error) {
      setShiftOptions([]);
      setShiftError(error.message || 'Failed to load available shifts.');
    } finally {
      setLoadingShifts(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    fetchShiftOptions();
  }, [fetchShiftOptions]);

  const mergeWithExisting = useCallback(
    (values) => ({
      ...(typeof data === 'object' && data !== null ? data : {}),
      ...values,
    }),
    [data],
  );

  const watchedShifts = watch('shifts', initialValues.shifts);

  const handleShiftsChange = useCallback(
    (nextEntries) => {
      setValue('shifts', nextEntries, { shouldDirty: true, shouldValidate: false });
      if (Array.isArray(nextEntries) && nextEntries.length > 0) {
        clearErrors('shifts');
      }
    },
    [setValue, clearErrors],
  );

  const buildWorkingTimePayload = useCallback(
    (formValues) => {
      const merged = mergeWithExisting(formValues);
      const normalizedShifts = normalizeShiftsForPayload(merged.shifts);
      const primaryShift =
        normalizedShifts.find((shift) => shift.isPrimary) ?? normalizedShifts[0] ?? null;

      return {
        ...merged,
        shifts: normalizedShifts,
        timeFrom: normalizeTimeForPayload(primaryShift?.timeFrom ?? merged.timeFrom),
        timeTo: normalizeTimeForPayload(primaryShift?.timeTo ?? merged.timeTo),
      };
    },
    [mergeWithExisting],
  );

  const onFormSubmit = useCallback(
    (formValues) => {
      const workingTime = buildWorkingTimePayload(formValues);

      if (enableValidation && (!workingTime.shifts || workingTime.shifts.length === 0)) {
        setError('shifts', { type: 'manual', message: 'Please add at least one working shift.' });
        return;
      }

      onSubmit({ workingTime });
    },
    [buildWorkingTimePayload, enableValidation, onSubmit, setError],
  );

  const handleBack = useCallback(() => {
    const currentValues = getValues();
    const workingTime = buildWorkingTimePayload(currentValues);
    onSubmit({ workingTime, goBack: true });
  }, [buildWorkingTimePayload, getValues, onSubmit]);

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      className="d-flex flex-column"
      style={{ minHeight: '80vh' }}
    >
      <div className="row flex-grow-1">
        <div className="col-md-8">
          <div className="row g-4">
            <div className="col-12">
              <div className="form-floating">
                <select
                  className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                  id="employmentType"
                  {...register('type')}
                >
                  <option value="">Select Type</option>
                  <option value="fullTime">Full Time</option>
                  <option value="partTime">Part Time</option>
                  <option value="contract">Contract</option>
                </select>
                <label htmlFor="employmentType">Select Employment Type</label>
                {errors.type && <div className="invalid-feedback">{errors.type.message}</div>}
              </div>
            </div>

            <div className="col-12">
              <WorkingShiftAllocation
                shifts={watchedShifts}
                onChange={handleShiftsChange}
                shiftOptions={shiftOptions}
                loading={loadingShifts}
                error={shiftError}
                validationError={errors.shifts?.message}
              />
              <p className="text-muted small mt-2">
                Add one or more shifts for this employee. Mark the primary shift to use for reporting.
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div
            className="position-fixed end-0 d-none d-md-block"
            style={{ maxWidth: '400px', zIndex: 1, bottom: '100px' }}
          >
            <img
              src={employeeForm7}
              alt="Working Time Information"
              className="img-fluid"
              style={{ maxHeight: '45vh' }}
            />
          </div>
        </div>
      </div>

      <div className="mt-auto pt-4 d-flex justify-content-between">
        <button type="button" onClick={handleBack} className="btn btn-outline-secondary px-4">
          Back
        </button>
        <button type="submit" className="btn btn-danger px-4">
          Next
        </button>
      </div>
    </form>
  );
};

export default WorkingTimeForm;
